import { useCallback, useState } from 'react';

import { SelectionBounds } from '../utils/selectionCalculations';

/**
 * Hook for keyboard navigation of selection
 */
export function useSelectionKeyboard(
  videoDuration: number,
  onSelectionMove?: (startTime: number) => void,
  onVideoTimeUpdate?: (time: number) => void,
  onPlayPause?: () => void,
  onEnsureSelectionVisible?: (overrideStartTime?: number) => void,
  containerRef?: React.RefObject<HTMLDivElement>
) {
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, selection: SelectionBounds) => {
      if (!onSelectionMove || videoDuration === 0) return;

      const currentStartTime = selection.startTime;
      let newStartTime = currentStartTime;
      let handled = true;

      switch (e.key) {
        case 'ArrowLeft': {
          e.preventDefault();
          const increment = e.shiftKey ? 1.0 : 0.1; // Shift for larger time jumps
          newStartTime = Math.max(0, currentStartTime - increment);
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          const increment = e.shiftKey ? 1.0 : 0.1; // Shift for larger time jumps
          newStartTime = Math.max(
            0,
            Math.min(
              videoDuration - selection.duration,
              currentStartTime + increment
            )
          );
          break;
        }
        case 'Home': {
          e.preventDefault();
          newStartTime = 0;
          break;
        }
        case 'End': {
          e.preventDefault();
          newStartTime = Math.max(0, videoDuration - selection.duration);
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          if (onPlayPause) {
            onPlayPause();
          }
          return;
        }
        case 'Backspace':
        case 'Delete': {
          e.preventDefault();
          newStartTime = 0;
          break;
        }
        default:
          handled = false;
      }

      if (handled && newStartTime !== currentStartTime) {
        onSelectionMove(newStartTime);

        // Sync video preview to the new selection position
        onVideoTimeUpdate?.(newStartTime);

        // Use requestAnimationFrame for better timing with large jumps
        requestAnimationFrame(() => {
          onEnsureSelectionVisible?.(newStartTime);

          // Small delay to ensure DOM updates are complete before focusing
          setTimeout(() => {
            // Ensure focus is retained after viewport changes
            if (containerRef?.current) {
              const selectionHandle = containerRef.current.querySelector(
                '.selection-handle'
              ) as HTMLElement;
              if (
                selectionHandle &&
                document.activeElement !== selectionHandle
              ) {
                selectionHandle.focus();
              }
            }
          }, 10); // Small delay to ensure scroll and DOM updates are complete
        });
      }
    },
    [
      videoDuration,
      onSelectionMove,
      onVideoTimeUpdate,
      onPlayPause,
      onEnsureSelectionVisible,
      containerRef,
    ]
  );

  const handleFocus = useCallback(() => {
    setIsKeyboardFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsKeyboardFocused(false);
  }, []);

  return {
    isKeyboardFocused,
    handleKeyDown,
    handleFocus,
    handleBlur,
  };
}
